import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

const ScoreChart = ({ data, period }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-400">
        <div className="text-center">
          <div className="text-2xl mb-2">📊</div>
          <div className="text-sm">No score data available</div>
          <div className="text-xs text-gray-500 mt-1">for {period}</div>
        </div>
      </div>
    );
  }

  // Sort data chronologically (oldest first) and transform for chart
  const sortedData = [...data].sort((a, b) => a.timestamp - b.timestamp);
  const chartData = sortedData.map(entry => ({
    ...entry,
    displayDate: new Date(entry.date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: data.length > 7 ? undefined : '2-digit'
    }),
    // Show score changes but handle zero changes appropriately
    positiveChange: entry.score_change > 0 ? entry.score_change : null,
    negativeChange: entry.score_change < 0 ? entry.score_change : null,
    noChange: entry.score_change === 0 ? entry.total_score : null
  }));

  const formatTooltip = (value, name, props) => {
    if (name === 'positiveChange') {
      return [`+${value.toFixed(4)}`, 'Score Gain'];
    }
    if (name === 'negativeChange') {
      return [`${value.toFixed(4)}`, 'Score Loss'];
    }
    if (name === 'noChange') {
      return [`${value.toFixed(4)}`, 'Initial Score'];
    }
    return [value, name];
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const entry = payload[0];
      
      return (
        <div className="bg-gray-800 border border-gray-600 rounded-lg p-3">
          <p className="text-gray-300 text-sm">{label}</p>
          <p className="text-white font-medium">
            Total Score: {data.total_score?.toFixed(4)}
          </p>
          
          {/* Show the bar value that's actually being hovered */}
          {entry.dataKey === 'positiveChange' && (
            <p className="text-sm text-green-400">
              Change: +{data.score_change.toFixed(4)}
            </p>
          )}
          {entry.dataKey === 'negativeChange' && (
            <p className="text-sm text-red-400">
              Change: {data.score_change.toFixed(4)}
            </p>
          )}
          {entry.dataKey === 'noChange' && (
            <p className="text-sm text-gray-400">
              Initial Score: +{data.total_score.toFixed(4)}
            </p>
          )}
          
          <p className="text-xs text-gray-400 capitalize">
            {data.change_type?.replace('_', ' ')}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis 
            dataKey="displayDate" 
            stroke="#9CA3AF"
            fontSize={12}
            tick={{ fill: '#9CA3AF' }}
          />
          <YAxis 
            stroke="#9CA3AF"
            fontSize={12}
            tick={{ fill: '#9CA3AF' }}
            tickFormatter={(value) => value.toFixed(2)}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine y={0} stroke="#6B7280" strokeDasharray="2 2" />
          
          {/* Positive changes (gains) */}
          <Bar
            dataKey="positiveChange"
            fill="#10B981"
            name="Score Gain"
          />
          
          {/* Negative changes (losses) */}
          <Bar
            dataKey="negativeChange"
            fill="#EF4444"
            name="Score Loss"
          />
          
          {/* Initial/No change values */}
          <Bar
            dataKey="noChange"
            fill="#6B7280"
            name="Initial Score"
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ScoreChart;
